import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axios from 'axios';
import jwt_decode from "jwt-decode";
import Navbar from '../../Navbar';
import Header from '../../Header';
import Loader from '../../Loader';
import baseUrl from '../../BaseUrl';
import TrendDeviceChart from './TrendDeviceChart';

const ENRMSDeviceComparison = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { compid, category, carrierPlatform, batches, persistedState, tags, tagOperator } = location.state || {};

    const formatDate = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        // Use toLocaleDateString for a more robust and localized date format
        return date.toLocaleDateString();
    };

    const [name, setName] = useState('');
    const [token, setToken] = useState('');
    const [expire, setExpire] = useState('');
    const [access, setAccess] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [numberDetails, setNumberDetails] = useState(null);
    const [batchHeaders, setBatchHeaders] = useState(null);

    useEffect(() => {
        refreshToken();
        getUserAccess();
    }, []);

    useEffect(() => {
        if (token && compid && category && carrierPlatform && batches) {
            fetchTrendDetails();
        } else if (!location.state) {
            // If there's no state, maybe redirect back or show an error
            navigate('/enrms-summary-comparison');
        }
    }, [token, compid, category, carrierPlatform, batches]);

    const refreshToken = async () => {
        try {
            const response = await axios.get(`${baseUrl}/token`);
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setName(decoded.name);
            setExpire(decoded.exp);
        } catch (error) {
            if (error.response) {
                navigate("/");
            }
        }
    };

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        const currentDate = new Date();
        if (expire * 1000 < currentDate.getTime()) {
            const response = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${response.data.accessToken}`;
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setName(decoded.name);
            setExpire(decoded.exp);
        }
        return config;
    }, (error) => {
        return Promise.reject(error);
    });

    const getUserAccess = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/user-access`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAccess(response.data.results);
        } catch (error) {
            console.log(error);
        }
    };

    const fetchTrendDetails = async () => {
        setPopupOpen(true);
        try {
            const response = await axiosJWT.post(`${baseUrl}/get-enrms-device-comparison`, {
                batches,
                category,
                carrierPlatform,
                tags,
                tagOperator
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNumberDetails(response.data.details);
            setBatchHeaders(response.data.batches);
        } catch (error) {
            console.error("Error fetching trend details:", error);
            alert("Failed to fetch trend details.");
        } finally {
            setPopupOpen(false);
        }
    };

    const goBack = () => {
        navigate('/enrms-summary-comparison', {
            state: {
                persistedState: persistedState,
                compid: compid
            }
        });
    };

    const handleExport = () => {
        if (!numberDetails || !batchHeaders || Object.keys(numberDetails).length === 0) {
            alert("No data to export.");
            return;
        }

        setPopupOpen(true);
        try {
            // Transform the data into an array of objects suitable for xlsx library
            const dataToExport = Object.keys(numberDetails).map(tn_number => {
                const row = { "Number": tn_number };
                Object.keys(batchHeaders).forEach(batchKey => {
                    const headerText = batchHeaders[batchKey];
                    row[headerText] = numberDetails[tn_number][batchKey];
                });
                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Device Comparison");
            XLSX.writeFile(workbook, "ENRMS_Device_Comparison.xlsx");
        } catch (error) {
            console.error("Error exporting data:", error);
            alert("Failed to export data.");
        } finally {
            setPopupOpen(false);
        }
    };

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {popupOpen && <Loader />}
                <div className="content-page admin-attendance-page">
                    <div className="row">
                        <div className="col-8">
                            <div className="left-title">
                                <h1>{carrierPlatform?.replace(/_/g, ' ')} - {category} Comparison
                                    <span style={{ display: 'block', fontSize: '14px', marginTop: '5px', fontWeight: 'normal' }}>
                                        {persistedState?.displayDate && persistedState?.displayEndDate && (
                                            <>
                                                Date Range: {formatDate(persistedState.displayDate)} - {formatDate(persistedState.displayEndDate)}
                                            </>
                                        )}
                                        {tags && tags.length > 0 && (
                                            <>
                                                <br />
                                                Tags: {tags.map(t => t.label).join(', ')} ({tagOperator})
                                            </>
                                        )}
                                    </span>
                                </h1>
                            </div>
                        </div>
                        <div className="col-4 inventory-btn">
                            <div className="addnew-button">
                                {numberDetails && Object.keys(numberDetails).length > 0 && (
                                    <a
                                        href="#"
                                        className="newrow"
                                        onClick={(e) => { e.preventDefault(); handleExport(); }}
                                        style={{ textDecoration: 'none', color: 'white', marginRight: '10px' }}
                                    >
                                        Export
                                    </a>
                                )}
                                <a href="#" onClick={goBack}>Back</a>
                            </div>
                        </div>
                    </div>

                    {numberDetails && batchHeaders && Object.keys(numberDetails).length > 0 ? (
                        <>
                            <div className="common-white-shadow-background setting-section">
                                <h3>Comparison Details</h3>
                                <div className="setting-section haft-whitebox cia-table">
                                    <table className="tablebox search-result-table inventory-table">
                                        <thead>
                                            <tr>
                                                <th>Number</th>
                                                {Object.values(batchHeaders).map(header => <th key={header}>{header}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.keys(numberDetails).map(tn_number => (
                                                <tr key={tn_number}>
                                                    <td><strong>{tn_number}</strong></td>
                                                    {Object.keys(batchHeaders).map(batchKey => (
                                                        <td key={`${tn_number}-${batchKey}`}>{numberDetails[tn_number][batchKey]}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <TrendDeviceChart details={numberDetails} batches={batchHeaders} />
                        </>
                    ) : !popupOpen && (
                        <div className="common-white-shadow-background setting-section">
                            <p>No numbers found for this category.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ENRMSDeviceComparison;
